import type { AnalyticsFlow } from 'pi-kiosk-shared';
import { ANALYTICS_DONATION_EVENTS, ANALYTICS_UNIVERSAL_EVENTS } from 'pi-kiosk-shared/analyticsEvents';
import type { CustomerEmitEventInput } from '../../shared/analytics/customerAnalyticsClient.js';

type DonationTrack = (input: CustomerEmitEventInput) => void;

export function emitDonationScreenViewed(
  track: DonationTrack,
  screenName: string,
  metadata: Record<string, unknown> = {}
): void {
  track({
    eventName: ANALYTICS_UNIVERSAL_EVENTS.SCREEN_VIEWED,
    flow: 'DONATION',
    screenName,
    metadata,
  });
}

export function emitDonationAmountSelected(
  track: DonationTrack,
  amountMinor: number,
  custom: boolean
): void {
  track({
    eventName: custom
      ? ANALYTICS_DONATION_EVENTS.DONATION_CUSTOM_AMOUNT_ENTERED
      : ANALYTICS_DONATION_EVENTS.DONATION_AMOUNT_SELECTED,
    flow: 'DONATION',
    screenName: 'donation_amount',
    metadata: { amountMinor },
  });
}

export function emitDonationProjectSelected(
  track: DonationTrack,
  donationProjectCode: string | null
): void {
  track({
    eventName: ANALYTICS_DONATION_EVENTS.DONATION_PROJECT_SELECTED,
    flow: 'DONATION',
    screenName: 'donation_project',
    metadata:
      donationProjectCode !== null
        ? { donationProjectCode }
        : {},
  });
}

export function donationStartedMetadata(
  donationProjectCode?: string | null
): Record<string, unknown> {
  return donationProjectCode !== undefined && donationProjectCode !== null && donationProjectCode.length > 0
    ? { donationProjectCode }
    : {};
}

export function donationFlow(): AnalyticsFlow {
  return 'DONATION';
}

export function resolveDonationProjectId(id: string | number): number | undefined {
  if (typeof id === 'number' && Number.isInteger(id)) {
    return id;
  }
  const parsed = Number(id);
  if (Number.isFinite(parsed) && Number.isInteger(parsed)) {
    return parsed;
  }
  return undefined;
}

export function emitDonationImpactOpened(track: DonationTrack, projectId: number): void {
  track({
    eventName: ANALYTICS_DONATION_EVENTS.DONATION_IMPACT_OPENED,
    flow: 'DONATION',
    screenName: 'donation_project',
    metadata: { project_id: projectId },
  });
}

export function emitDonationTaxReceiptSelected(track: DonationTrack, enabled: boolean): void {
  track({
    eventName: ANALYTICS_DONATION_EVENTS.DONATION_TAX_RECEIPT_SELECTED,
    flow: 'DONATION',
    screenName: 'donation_method',
    metadata: { enabled },
  });
}

export function emitRecurringDonationSelected(
  track: DonationTrack,
  recurrencePeriod: string,
): void {
  track({
    eventName: ANALYTICS_DONATION_EVENTS.RECURRING_DONATION_SELECTED,
    flow: 'DONATION',
    screenName: 'donation_method',
    metadata: { recurrence_period: recurrencePeriod },
  });
}

export function emitDonationAbandoned(
  track: DonationTrack,
  screenName: 'donation_amount' | 'donation_project' | 'donation_method',
  metadata: { project_id?: number; amount_cents?: number } = {},
): void {
  track({
    eventName: ANALYTICS_DONATION_EVENTS.DONATION_ABANDONED,
    flow: 'DONATION',
    screenName,
    metadata,
  });
}
